import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { inventaireApi } from "./inventaireApi";
import { feedback, useAppShell } from "./appShell";
import { IconBox, IconChevronLeft, IconPlay, IconWarning } from "./icons";
import { badgeInventaire, badgeLigne, perimetreLong } from "./statuts";
import "./inventaire.css";

export default function InventairePage() {
  const USERNAME = "RESPONSABLE_STOCK";
  const { id } = useParams();
  const navigate = useNavigate();
  const { setPending } = useAppShell();

  const [inventaire, setInventaire] = useState(null);
  const [numero, setNumero] = useState("");
  const [status, setStatus] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [doublon, setDoublon] = useState("");
  const [flash, setFlash] = useState(false);

  const inputRef = useRef(null);
  const flashTimer = useRef(null);

  /* ================= LOCAL STORAGE ================= */

  const localKey = (invId) => `inventaire_${invId}_scans`;

  const saveLocal = (invId, data) => {
    localStorage.setItem(localKey(invId), JSON.stringify(data));
  };

  const loadLocal = (invId) => {
    const data = localStorage.getItem(localKey(invId));
    if (!data) return [];
    // Une ligne « syncing » relue depuis le cache correspond a un scan interrompu
    // (fermeture de l'app en plein envoi) : on retire le drapeau, sinon son
    // spinner tournerait indefiniment. Il n'y a toujours pas de file de reprise.
    return JSON.parse(data).map(({ syncing, ...l }) =>
      syncing ? { ...l, erreur: "Synchronisation interrompue" } : l
    );
  };

  const clearLocal = (invId) => {
    localStorage.removeItem(localKey(invId));
  };

  /* ================= LOAD INVENTAIRE ================= */

  useEffect(() => {
    if (id) loadInventaire(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInventaire = async (invId) => {
    const res = await inventaireApi.getById(invId);
    setInventaire(res.data);

    const local = loadLocal(invId);
    setLignes(local);

    await loadStatus(invId);
  };

  /* ================= CREATE ================= */

  const createInventaire = async () => {
    const res = await inventaireApi.create(USERNAME);
    setInventaire(res.data);
    setLignes([]);
    await loadStatus(res.data.id);
  };

  /* ================= START ================= */

  const startInventaire = async () => {
    await inventaireApi.start(inventaire.id, USERNAME);
    await loadStatus(inventaire.id);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  /* ================= STATUS AUTO REFRESH ================= */

  const loadStatus = async (invId) => {
    const res = await inventaireApi.getStatus(invId);
    setStatus(res.data);

    // clean cache when finished
    if (res.data.statut === "VALIDE") {
      clearLocal(invId);
    }
  };

  useEffect(() => {
    if (!inventaire?.id) return;

    const interval = setInterval(() => {
      loadStatus(inventaire.id);
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventaire]);

  /* ================= BANDEAU DE SYNCHRO ================= */

  // Les scans encore en vol remontent a la coquille : combines a la perte du
  // reseau, c'est ce que l'operateur doit voir sans quitter l'ecran de scan.
  const enAttente = useMemo(() => lignes.filter((l) => l.syncing).length, [lignes]);

  useEffect(() => {
    setPending(enAttente);
  }, [enAttente, setPending]);

  useEffect(
    () => () => {
      setPending(0);
      clearTimeout(flashTimer.current);
    },
    [setPending]
  );

  /* ================= SCAN ================= */

  const scanRouleau = async () => {
    if (!numero || status?.statut !== "EN_COURS") return;

    const scanne = numero;

    // prevent duplicates
    if (lignes.some((l) => l.numeroRouleau === scanne)) {
      // Message inline plutot qu'une alert() : une modale bloque la douchette,
      // qui continue d'emettre des caracteres pendant que le focus est perdu.
      setDoublon(scanne);
      setNumero("");
      return;
    }

    const localId = Date.now();
    const newScan = {
      id: localId,
      numeroRouleau: scanne,
      syncing: true,
    };

    const updated = [newScan, ...lignes];

    // instant UI update
    setLignes(updated);
    saveLocal(inventaire.id, updated);
    setNumero("");
    setDoublon("");

    feedback();
    setFlash(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(false), 400);

    // Sync backend. Le retour porte le STATUT reel de la ligne (OK / HORS_PERIMETRE / INCONNU) :
    // on remplace la ligne optimiste par celle du serveur, sinon l'operateur ne saurait pas qu'il
    // vient de scanner un rouleau d'un autre import.
    try {
      const res = await inventaireApi.scan(inventaire.id, scanne, USERNAME);
      const ligneServeur = res?.data;

      remplacerLigne(localId, {
        id: localId,
        numeroRouleau: ligneServeur?.numeroRouleau || scanne,
        statut: ligneServeur?.statut,
        tissu: ligneServeur?.tissu,
        commentaire: ligneServeur?.commentaire,
      });

      await loadStatus(inventaire.id);
    } catch (err) {
      // Le backend renvoie {"error": "..."} sur un refus metier (code trop long, rouleau non
      // disponible, inventaire non demarre). Ces messages etaient jusqu'ici avales par un
      // console.error : l'operateur voyait sa ligne s'afficher comme scannee alors que rien
      // n'avait ete enregistre.
      const message =
        err?.response?.data?.error ||
        (err?.response ? `Erreur serveur (${err.response.status})` : "Réseau indisponible");

      remplacerLigne(localId, {
        id: localId,
        numeroRouleau: scanne,
        erreur: message,
      });
    }
  };

  /** Remplace une ligne (identifiee par son id local) dans l'etat ET dans le cache local. */
  const remplacerLigne = (localId, ligne) => {
    setLignes((precedentes) => {
      const suivantes = precedentes.map((l) => (l.id === localId ? ligne : l));
      saveLocal(inventaire.id, suivantes);
      return suivantes;
    });
  };

  /* ================= UI HELPERS ================= */

  const avancement = useMemo(() => {
    if (!status) return "";
    return `${status.scanned ?? 0} / ${status.total ?? 0}`;
  }, [status]);

  const progression = useMemo(() => {
    if (!status) return 0;
    if (typeof status.progress === "number") return Math.min(100, status.progress);
    if (!status.total) return 0;
    return Math.min(100, Math.round(((status.scanned ?? 0) / status.total) * 100));
  }, [status]);

  const perimetre = useMemo(() => perimetreLong(status || inventaire), [status, inventaire]);

  // Portee de l'inventaire courant, pour nommer precisement un HORS_PERIMETRE (« AUTRE IMPORT »
  // vs « AUTRE TISSU »). Meme source que le sous-titre : le /status sonde, a defaut l'en-tete.
  const portee = useMemo(
    () => (status || inventaire)?.portee,
    [status, inventaire]
  );

  /* ================= UI ================= */

  if (!inventaire) {
    return (
      <div>
        <div className="page-header">
          <button className="btn-back" onClick={() => navigate("/inventaires")}>
            <IconChevronLeft />
            Retour
          </button>
          <div className="page-header-title">Nouvel Inventaire</div>
        </div>

        <div className="inv-center-card">
          <div className="inv-center-icon">
            <IconBox size={26} />
          </div>

          <button className="btn btn-gold" onClick={createInventaire}>
            + Créer Inventaire global
          </button>

          {/* La douchette ne cree que des inventaires GLOBAUX. Un inventaire borne a un import
              se cree depuis l'ERP (ecran Inventaires), qui seul permet de choisir l'import. */}
          <p className="inv-hint">
            Pour inventorier un import précis, créez l'inventaire depuis l'ERP,
            puis ouvrez-le dans la liste.
          </p>
        </div>
      </div>
    );
  }

  const badgeStatut = badgeInventaire(status?.statut);

  return (
    <div>
      {/* HEADER */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate("/inventaires")}>
          <IconChevronLeft />
          Retour
        </button>

        <div>
          <div className="page-header-code">
            {inventaire.code || `Inventaire #${inventaire.id}`}
          </div>

          <div className="page-header-sub">
            {perimetre} • {avancement}
          </div>
        </div>
      </div>

      <div className="inv-body">
        {/* STATUS */}
        {status && (
          <div className="card inv-progress-card">
            <div className="inv-status-row">
              <span className={`pill pill--${badgeStatut.variant}`}>{badgeStatut.label}</span>

              <span className="inv-progress-text">
                {status.scanned} / {status.total}
              </span>
            </div>

            <div className="inv-progress">
              <div style={{ width: `${progression}%` }} />
            </div>
          </div>
        )}

        {/* START BUTTON */}
        {status?.statut === "BROUILLON" && (
          <button
            className="btn btn-blue"
            style={{ marginBottom: 16 }}
            onClick={startInventaire}
          >
            <IconPlay />
            Ouvrir Inventaire
          </button>
        )}

        {/* SCAN INPUT */}
        {status?.statut === "EN_COURS" && (
          <div className="scan-field inv-scan-field">
            <div className={`scan-flash ${flash ? "on" : ""}`} />
            <input
              ref={inputRef}
              type="text"
              inputMode="none"
              placeholder="Scanner numéro rouleau..."
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && scanRouleau()}
              autoFocus
            />
          </div>
        )}

        {doublon && (
          <div className="alert-error" style={{ marginTop: 0, marginBottom: 16 }}>
            <IconWarning />
            Rouleau {doublon} déjà scanné
          </div>
        )}

        {/* LIST */}
        <div className="card">
          <div className="inv-list-head">
            <h3>Rouleaux scannés</h3>
            <span className="inv-list-meta">{lignes.length}</span>
          </div>

          {lignes.length === 0 && (
            <div className="inv-empty-inline">Aucun rouleau scanné.</div>
          )}

          <div className="scan-lines">
            {lignes.map((l) => {
              const badge = l.statut ? badgeLigne(l.statut, portee) : null;
              const variante = l.erreur ? "danger" : badge ? badge.variant : "neutral";
              // Le repli est generique : le motif exact (autre import / autre tissu) est compose
              // cote serveur dans `commentaire`, qui prime.
              const note =
                l.erreur ||
                l.commentaire ||
                (l.statut === "HORS_PERIMETRE" ? "Rouleau étranger au périmètre" : "");

              return (
                <div key={l.id} className={`scan-line ${variante}`}>
                  <div className="scan-line-main">
                    <span className="scan-line-numero">{l.numeroRouleau}</span>

                    {l.syncing && <span className="spinner spinner--muted" />}

                    {badge && (
                      <span className={`pill pill--sm pill--pop pill--${badge.variant}`}>
                        {badge.label}
                      </span>
                    )}
                  </div>

                  {/* Motif du refus, ou provenance d'un rouleau hors perimetre. */}
                  {note && <div className="scan-line-note">{note}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
